// MuteContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { debugLog } from "../../common/utils/debug";

type MuteContextType = {
  isMuted: boolean;
  isManuallyMuted: boolean;
  toggleMute: () => void;
  setMuted: (value: boolean) => void;
  /** Auto-unmute that respects manual mute - won't unmute if user manually muted */
  autoUnmute: () => void;
  /** Force unmute - clears manual mute flag and unmutes (for deliberate user actions like Continue Interview) */
  forceUnmute: () => void;
};

const MuteContext = createContext<MuteContextType | undefined>(undefined);

export const MuteProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isMuted, setIsMuted] = useState(true); // Start muted by default
  const [isManuallyMuted, setIsManuallyMuted] = useState(false); // Track if user manually muted

  // Toggle mute - this is a manual action by the user
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newValue = !prev;
      // If user is muting, mark as manually muted
      // If user is unmuting, clear the manual mute flag
      setIsManuallyMuted(newValue);
      return newValue;
    });
  }, []);

  // Set muted directly - respects manual mute when trying to unmute
  const setMuted = useCallback((value: boolean) => {
    if (value) {
      // Muting - always allow, don't change manual flag
      setIsMuted(true);
    } else {
      // Unmuting - respect manual mute flag
      if (isManuallyMuted) {
        debugLog('🔇 [MuteContext] Skipping setMuted(false) - user manually muted');
        return;
      }
      setIsMuted(false);
    }
  }, [isManuallyMuted]);

  // Auto-unmute that respects manual mute preference
  const autoUnmute = useCallback(() => {
    if (!isManuallyMuted) {
      setIsMuted(false);
    } else {
      debugLog('🔇 [MuteContext] Skipping auto-unmute - user manually muted');
    }
  }, [isManuallyMuted]);

  // Force unmute - clears manual mute flag and unmutes
  // Used for deliberate user actions like "Continue Interview"
  const forceUnmute = useCallback(() => {
    debugLog('🎤 [MuteContext] Force unmute - clearing manual mute flag');
    setIsManuallyMuted(false);
    setIsMuted(false);
  }, []);

  useEffect(() => {}, [isMuted]);

  return (
    <MuteContext.Provider value={{ isMuted, isManuallyMuted, toggleMute, setMuted, autoUnmute, forceUnmute }}>
      {children}
    </MuteContext.Provider>
  );
};

export const useMute = () => {
  const ctx = useContext(MuteContext);
  if (!ctx) throw new Error("useMute must be used inside MuteProvider");
  return ctx;
};
